import React, { useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { observer } from 'mobx-react';
import { formatMessage } from '../../../translations/format-message';
import { FormattedMessage } from 'react-intl';
import { useLedSerial } from '../hooks/use-led-serial';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';

interface LEDLengthProps {
  ledLengthList: any[];
  isTimerRunning: boolean;
  setLedLengthListState: React.Dispatch<React.SetStateAction<any[]>>;
  openLEDHandler: (event: any) => void;
}

// DB value is in mm (e.g. "103.0000"); display it without the decimal part
const formatLength = (val: any) => {
  if (val === null || val === undefined || val === '') return '';
  const num = Number(val);
  return !isNaN(num) ? String(Math.trunc(num)) : String(val);
};

const LEDLength: React.FC<LEDLengthProps> = ({
  isTimerRunning,
  ledLengthList,
  setLedLengthListState,
  openLEDHandler,
}) => {
  const { sendCommand } = useLedSerial();
  const { productStore } = useStore();
  const { GetFactorySettingService } = productStore;

  useEffect(() => {
    const tenantId = Number(getTenantIdFromLocalStorage());
    if (tenantId) {
      GetFactorySettingService(tenantId);
    }
  }, [GetFactorySettingService]);

  /**
   * Send LED values to the device and show the acknowledgement/error
   */
  const handleLEDClick = async (item: any, time: number) => {
    try {
      const tenantId = Number(getTenantIdFromLocalStorage());
      let limit = productStore.ledLength;
      if (tenantId) {
        const fetched = await GetFactorySettingService(tenantId);
        if (typeof fetched === 'number') {
          limit = fetched;
        } else {
          limit = productStore.ledLength;
        }
      }

      const min = Number(((Number(item.MinValue) || 0) / 10).toFixed(4));
      const cent = Number(((Number(item.CenterValue) || 0) / 10).toFixed(4));
      const max = Number(((Number(item.MaxValue) || 0) / 10).toFixed(4));

      if (
        limit > 0 &&
        (min > limit || cent > limit || max > limit)
      ) {
        toast.error(formatMessage('led_error_length_out_of_bounds'));
        return;
      }
      /**
       * Example:
       * 1000,30,G,100,R,150,G,200
       */
      const message = `${limit},${time},G,${min},R,${cent},G,${max}\r\n`;

      const response = await sendCommand(message);
      console.log('LED Cut device response:', response);

      toast.success(formatMessage('led_command_success'));
    } catch (error: any) {
      console.error('Serial error:', error);
      toast.error(error?.message || formatMessage('led_error_unknown'));
    }
  };

  return (
    <div className="tableDevices product-img tableDesign">
      {ledLengthList && ledLengthList.length > 0 ? (
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <table className="table table-hover table-bordered">
              <thead>
                <tr>
                  <th>{formatMessage('led_title')}</th>
                  <th>{formatMessage('led_id')}</th>
                  <th>{formatMessage('min_tolerance')} (mm)</th>
                  <th>{formatMessage('center_value')} (mm)</th>
                  <th>{formatMessage('max_tolerance')} (mm)</th>
                </tr>
              </thead>
              <tbody>
                {ledLengthList.map((item, index) => (
                  <tr key={`${index + 1}`}>
                    <td>
                      <Button
                        className="btn btn-secondary btn-sm"
                        data-toggle="modal"
                        data-testid="modalbtn"
                        data-target="#addUser"
                        onClick={() => handleLEDClick(item, -1)}
                        disabled={!isTimerRunning}
                      >
                        <FormattedMessage id="led_title" />
                      </Button>
                    </td>
                    <td>{item.LEDId}</td>
                    <td>{formatLength(item.MinValue)}</td>
                    <td>{formatLength(item.CenterValue)}</td>
                    <td>{formatLength(item.MaxValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-row d-flex">
          <span className="empty-message text-center w-100">
            {formatMessage('no_led_length_data_available')}
          </span>
        </div>
      )}
    </div>
  );
};

export default observer(LEDLength);
